import React, { useContext, useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import DateInputProvider from '../../contextProviders/DateInputProvider';
import GqlClientProvider from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import { AccountTodayOverrideContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import CommitmentQueryProvider from '../../contextProviders/CommitmentQueryProvider';
import CustomerLaneSlideOutProvider, {
  useLaneParams,
} from '../../contextProviders/CustomerLaneSlideOutProvider';
import FilterPlatesProvider from '../../contextProviders/FilterPlatesProvider';
import CustomerLaneBoardPeriodsProvider, {
  DEFAULT_CUSTOMER_LANE_PERIODS_DATE_RANGE,
} from '../../contextProviders/CustomerLaneBoardPeriodsProvider';
import BoardContext from '../../contexts/BoardContext';
import CustomerLaneCommitsContext from '../../contexts/CustomerLaneCommitsContext';
import DateInputContext from '../../contexts/DateInputContext';
import MetricsGate from 'components/MetricsGate';
import useToFixedFilters from 'hooks/useToFixedFilters';
import CustomerLaneCommitBoard from './CustomerLaneCommitBoard';
import useQuickFilters from '../../hooks/useQuickFilters';
import useReasonCodes from '../../hooks/useReasonCodes';
import useBoardOpenedTracking from '../../hooks/useBoardTracking';
import useFilteringState from '../PerformanceBoards/hooks/useFilteringState';
import { DATA_TYPE } from './constants';

const DATA_TYPES = [DATA_TYPE];

const CustomerLaneCommitBoardContainer = ({
  board,
  onDeleted,
  isDeletable,
  initialRunTimeFilters,
  initialScopeFilters,
}: {
  board: CustomerLaneBoard;
  onDeleted?: () => void;
  isDeletable?: boolean;
  initialRunTimeFilters?: FilterPlateType[];
  initialScopeFilters?: FilterPlateType[];
}) => {
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { setDateRange } = useContext(DateInputContext);
  const quickFilters = useQuickFilters(
    `${board.dataType} - board: ${board.id}`,
  );
  const [selectedLane, setSelectedLane] = useState<string | undefined>();
  const reasonCodes = useReasonCodes(board.dataType);
  const toDrillDowns = useToFixedFilters();

  const { onManualFilterChanged, onQuickFilterChanged } =
    useBoardOpenedTracking({ board });

  const [commitmentSlideoutParams, setCommitmentSlideoutParams] =
    useState<CommitmentSlideoutParams>({});

  const { lane } = useLaneParams({ commitmentSlideoutParams });
  const {
    selectedQuickFilters,
    setSelectedQuickFilters,
    netQuickFilters,
    drillDowns,
    setDrillDowns,
  } = useFilteringState({
    board,
    popupFilters: initialRunTimeFilters,
    scopeFilters: initialScopeFilters,
  });
  useEffect(() => {
    if (todayOverride) {
      const mockedToday = DateTime.fromISO(todayOverride);
      setDateRange({
        startDate: mockedToday.minus({ weeks: 13 }).toISODate(),
        endDate: mockedToday.toISODate(),
      });
    }
  }, [todayOverride, setDateRange]);

  useEffect(() => {
    setSelectedLane(lane);
  }, [lane]);

  return (
    <CustomerLaneBoardPeriodsProvider board={board}>
      <FilterPlatesProvider
        drillDowns={drillDowns}
        setDrillDowns={setDrillDowns}
        scope={initialScopeFilters}
        dataTypes={DATA_TYPES}
        quickFilters={netQuickFilters}
      >
        <div data-testid="popup-board" />
        <CustomerLaneCommitsContext.Provider
          value={{
            drillDowns: toDrillDowns({
              plates: drillDowns,
              variableDrillDowns: window.emptyArray,
            }),
            selectedLane,
            commitmentSlideoutParams,
            setCommitmentSlideoutParams,
          }}
        >
          <BoardContext.Provider
            value={{
              board,
              reasonCodes,
              isDeletable,
              onDeleted,
              quickFilters,
              selectedQuickFilters,
              setSelectedQuickFilters,
            }}
          >
            <MetricsGate content={board} contentType="Board">
              <GqlClientProvider
                visType={'customerLaneBoard'}
                visName={board.name}
                visId={board.id}
              >
                <CommitmentQueryProvider>
                  <CustomerLaneSlideOutProvider>
                    <CustomerLaneCommitBoard
                      board={board}
                      onManualFilterChanged={onManualFilterChanged}
                      onQuickFilterChanged={onQuickFilterChanged}
                    />
                  </CustomerLaneSlideOutProvider>
                </CommitmentQueryProvider>
              </GqlClientProvider>
            </MetricsGate>
          </BoardContext.Provider>
        </CustomerLaneCommitsContext.Provider>
      </FilterPlatesProvider>
    </CustomerLaneBoardPeriodsProvider>
  );
};

const CustomerLaneCommitBoardWithDate = ({
  board,
  onDeleted,
  isDeletable,
  initialRunTimeFilters,
  initialScopeFilters,
}: {
  board: CustomerLaneBoard;
  onDeleted?: () => void;
  isDeletable?: boolean;
  initialRunTimeFilters?: FilterPlateType[];
  initialScopeFilters?: FilterPlateType[];
}) => (
  <DateInputProvider
    initialDataTypes={DATA_TYPES}
    initialDateField="date"
    initialAdvancedRelativeDateRange={
      board.relativeDateRange ?? DEFAULT_CUSTOMER_LANE_PERIODS_DATE_RANGE
    }
  >
    <CustomerLaneCommitBoardContainer
      board={board}
      onDeleted={onDeleted}
      isDeletable={isDeletable}
      initialRunTimeFilters={initialRunTimeFilters}
      initialScopeFilters={initialScopeFilters}
    />
  </DateInputProvider>
);

export default CustomerLaneCommitBoardWithDate;
